import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  MessageCircle,
  CheckCircle2,
  Users,
  Clock,
  Award,
  ClipboardList,
  PlayCircle,
  Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import IntakeBanner from '../components/IntakeBanner';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import BookTrialButton from '../components/BookTrialButton';

const WHATSAPP_TRAINING = `https://wa.me/60166291687?text=${encodeURIComponent(
  "Hi Kalah, I'm interested in your Phonics Teacher Training programme. Could you share the upcoming intake details, format, and how to enrol?"
)}`;

const audiences = [
  {
    title: 'Early childhood teachers',
    description:
      'Preschool, kindergarten and primary teachers who want a clear, structured way to teach phonics and early reading.'
  },
  {
    title: 'Assistant teachers & aspiring educators',
    description:
      'Newcomers to early childhood education who want a confident, child-friendly methodology to start with.'
  },
  {
    title: 'Homeschool parents',
    description:
      'Parents teaching their own children — or a small co-op — who want a proven, gentle approach instead of guesswork.'
  },
  {
    title: 'Tuition teachers & literacy support',
    description:
      'Educators who work with children who struggle with reading and need a step-by-step method that actually clicks.'
  }
];

const modules = [
  'How young children actually learn to read',
  'Pure letter sounds — taught correctly (no “buh”, “kuh”, “muh”)',
  'Sound awareness, blending and segmenting',
  'CVC words and word-building activities',
  'First reading steps and reading readiness',
  'Common reading mistakes and how to prevent them',
  'Multisensory, movement-based activities',
  'Simple phonics lesson planning you can use the next day',
  'Working with children who struggle or feel anxious',
  'Supporting parents to continue the work at home'
];

const formatHighlights = [
  {
    icon: Clock,
    title: 'Flexible duration',
    description:
      'Modular sessions you can pace over a few weeks — designed to fit alongside a teaching schedule.'
  },
  {
    icon: PlayCircle,
    title: 'Live online sessions',
    description:
      'Interactive live sessions with real-time Q&A, demonstrations, and discussion.'
  },
  {
    icon: ClipboardList,
    title: 'Lesson-planning support',
    description:
      'Templates, sample weekly plans, and feedback on the lessons you put together yourself.'
  },
  {
    icon: Award,
    title: 'Certificate of participation',
    description:
      'Receive a signed certificate from Read Right Academy upon completion.'
  },
  {
    icon: Users,
    title: 'Practical demonstrations',
    description:
      'Watch real classroom activities, then practise and get feedback in a small group setting.'
  },
  {
    icon: Sparkles,
    title: 'Personal mentoring',
    description:
      'Direct access to Kalah throughout the programme — your questions are part of the learning.'
  }
];

const TeacherTrainingPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Phonics Teacher Training | Read Right Academy';
    return () => {
      document.title = 'Read Right Academy';
    };
  }, []);

  return (
    <>
      <IntakeBanner />
      <Header />
      <main className="bg-white pt-44">
        {/* Breadcrumb */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <Link
            to="/"
            data-testid="training-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#3B4BA0] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
          <div className="inline-block bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            For Teachers, Parents &amp; Aspiring Educators
          </div>
          <h1
            data-testid="training-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-5"
          >
            Phonics Teacher Training
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl">
            A practical, child-centred training programme that gives you the
            clarity, methodology and confidence to teach phonics and early
            reading the way young children actually learn. Designed and
            delivered by Kalah K. Singam, drawing on 20+ years in early
            childhood education and Montessori practice.
          </p>

          <div className="flex flex-wrap gap-3">
            <a
              href={WHATSAPP_TRAINING}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="training-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              <MessageCircle className="h-5 w-5" />
              Enquire on WhatsApp
            </a>
            <Link
              to="/#contact"
              data-testid="training-contact-link"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#3B4BA0] text-[#3B4BA0] hover:bg-[#3B4BA0] hover:text-white font-semibold px-6 py-3.5 rounded-xl transition-all"
            >
              All contact options
            </Link>
          </div>
        </section>

        {/* Who is it for */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Who is this training for?
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {audiences.map((aud) => (
              <div
                key={aud.title}
                className="bg-white rounded-2xl border-2 border-gray-100 hover:border-[#3B4BA0] transition-colors p-6"
              >
                <h3 className="font-bold text-gray-900 text-lg mb-2">
                  {aud.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {aud.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* What you'll learn */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              What you&apos;ll learn
            </h2>
            <p className="text-gray-600 mb-8 max-w-3xl">
              A clear, structured roadmap — from understanding how children
              learn to building lesson plans you can use the very next week.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100">
              {modules.map((mod, idx) => (
                <div
                  key={idx}
                  data-testid={`training-module-${idx}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 leading-snug">{mod}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Format */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              How the training is delivered
            </h2>
            <p className="text-gray-600">
              Designed for working teachers and busy parents — practical,
              flexible, and rooted in real classroom experience.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {formatHighlights.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:shadow-lg hover:border-teal-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Upcoming intake */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="rounded-3xl bg-gradient-to-br from-[#3B4BA0] to-teal-600 p-8 sm:p-12 text-white shadow-xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-2xl">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                  Upcoming intake — limited seats
                </h2>
                <p className="text-blue-50 leading-relaxed">
                  We accept a small cohort each intake so every participant
                  receives personal feedback and mentoring. Message us on
                  WhatsApp for the current schedule, fees, and how to reserve
                  your seat.
                </p>
              </div>
              <a
                href={WHATSAPP_TRAINING}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="training-whatsapp-cta-bottom"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#3B4BA0] hover:bg-gray-100 font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-2xl transition-all whitespace-nowrap"
              >
                <MessageCircle className="h-5 w-5" />
                Message us on WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Testimonials placeholder note */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
          <p className="text-sm text-gray-500 italic">
            Feedback from past participants will be added here soon — we are
            collecting testimonials from teachers who have completed the
            training.
          </p>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BookTrialButton />
    </>
  );
};

export default TeacherTrainingPage;
